import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  RefreshCw,
  Edit2,
  RotateCcw,
  Check,
  X,
  AlertCircle,
  Users,
  ArrowUpDown
} from 'lucide-react'
import { AdminService } from '../../services/adminService'

const PharmacistManagement = () => {
  const navigate = useNavigate()
  const [pharmacists, setPharmacists] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [sortByStatus, setSortByStatus] = useState(false) // false = alphabetical, true = online first

  // Modals
  const [editLimitModal, setEditLimitModal] = useState(false)
  const [resetCounterModal, setResetCounterModal] = useState(false)
  const [selectedPharmacist, setSelectedPharmacist] = useState(null)
  const [newDailyLimit, setNewDailyLimit] = useState('')
  const [newHourlyLimit, setNewHourlyLimit] = useState('')

  // Loading states
  const [updatingLimit, setUpdatingLimit] = useState(false)
  const [resettingCounter, setResettingCounter] = useState(false)
  const [togglingStatus, setTogglingStatus] = useState(null)

  // Roles
  const currentUser = AdminService.getCurrentUser()
  const isSuperAdmin = currentUser?.role === 'super_admin'
  const canEditDailyLimit = isSuperAdmin
  const canEditHourlyLimit = isSuperAdmin
  const canEditLimits = canEditDailyLimit || canEditHourlyLimit

  // Load pharmacists
  const loadPharmacists = async (silent = false) => {
    if (!silent) setLoading(true)
    setError('')

    try {
      const result = await AdminService.getAllPharmacists()
      if (result.success) {
        setPharmacists(result.data || [])
      } else {
        setError(result.error || 'Failed to load pharmacists')
      }
    } catch (err) {
      setError('An error occurred while loading pharmacists')
      console.error('Load pharmacists error:', err)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => {
    loadPharmacists()
  }, [])

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000)
      return () => clearTimeout(timer)
    }
  }, [success])

  // Sort pharmacists
  const getSortedPharmacists = () => {
    const sorted = [...pharmacists]
    if (sortByStatus) {
      // Sort by online status first (online first), then alphabetically
      sorted.sort((a, b) => {
        if (a.is_online === b.is_online) {
          return a.username.localeCompare(b.username)
        }
        return a.is_online ? -1 : 1
      })
    } else {
      // Sort alphabetically by username
      sorted.sort((a, b) => a.username.localeCompare(b.username))
    }
    return sorted
  }

  // Toggle online/offline status with optimistic update
  const handleToggleStatus = async (pharmacist) => {
    const newStatus = !pharmacist.is_online
    setError('')
    setSuccess('')
    setTogglingStatus(pharmacist.id)

    // Optimistic update - immediately update UI
    setPharmacists(prev =>
      prev.map(p =>
        p.id === pharmacist.id
          ? { ...p, is_online: newStatus }
          : p
      )
    )

    try {
      const result = await AdminService.togglePharmacistStatus(pharmacist.id, newStatus)

      if (result.success && result.verified && result.is_online === newStatus) {
        setPharmacists(prev =>
          prev.map(p =>
            p.id === pharmacist.id
              ? { ...p, is_online: result.is_online }
              : p
          )
        )
        setSuccess(`${pharmacist.username} is now ${result.is_online ? 'online' : 'offline'}`)
        // Silently refresh to get any server-side updates
        setTimeout(() => loadPharmacists(true), 500)
      } else {
        // Revert optimistic update on error, or use the verified Supabase value if available.
        const restoredStatus = typeof result.is_online === 'boolean' ? result.is_online : !newStatus
        setPharmacists(prev =>
          prev.map(p =>
            p.id === pharmacist.id
              ? { ...p, is_online: restoredStatus }
              : p
          )
        )
        setError(result.error || 'Failed to verify status update in Supabase')
      }
    } catch (err) {
      // Revert optimistic update on error
      setPharmacists(prev =>
        prev.map(p =>
          p.id === pharmacist.id
            ? { ...p, is_online: !newStatus }
            : p
        )
      )
      setError('An error occurred while updating status')
      console.error('Toggle status error:', err)
    } finally {
      setTogglingStatus(null)
    }
  }

  // Open edit limit modal
  const handleEditLimit = (pharmacist) => {
    setSelectedPharmacist(pharmacist)
    setNewDailyLimit(pharmacist.daily_limit)
    setNewHourlyLimit(pharmacist.hourly_limit ?? 12)
    setEditLimitModal(true)
    setError('')
  }

  // Save dispatch limits
  const handleSaveLimit = async () => {
    setError('')
    setSuccess('')

    const nextDailyLimit = parseInt(newDailyLimit, 10)
    const nextHourlyLimit = parseInt(newHourlyLimit, 10)

    if (canEditDailyLimit && (!Number.isInteger(nextDailyLimit) || nextDailyLimit < 1 || nextDailyLimit > 999)) {
      setError('Daily limit must be between 1 and 999')
      return
    }

    if (canEditHourlyLimit && (!Number.isInteger(nextHourlyLimit) || nextHourlyLimit < 1 || nextHourlyLimit > 999)) {
      setError('Hourly limit must be between 1 and 999')
      return
    }

    setUpdatingLimit(true)

    try {
      const result = await AdminService.updatePharmacistDispatchLimits(selectedPharmacist.id, {
        dailyLimit: canEditDailyLimit ? nextDailyLimit : undefined,
        hourlyLimit: canEditHourlyLimit ? nextHourlyLimit : undefined
      })

      if (result.success) {
        const updatedParts = []
        if (canEditDailyLimit) updatedParts.push(`daily ${nextDailyLimit}`)
        if (canEditHourlyLimit) updatedParts.push(`hourly ${nextHourlyLimit}`)

        setSuccess(`Limits for ${selectedPharmacist.username} updated: ${updatedParts.join(', ')}`)
        setEditLimitModal(false)
        setSelectedPharmacist(null)
        setNewDailyLimit('')
        setNewHourlyLimit('')
        loadPharmacists(true) // Silent refresh
      } else {
        setError(result.error || 'Failed to update limits')
      }
    } catch (err) {
      setError('An error occurred while updating limits')
      console.error('Update limit error:', err)
    } finally {
      setUpdatingLimit(false)
    }
  }

  // Open reset counter modal
  const handleResetCounter = (pharmacist) => {
    setSelectedPharmacist(pharmacist)
    setResetCounterModal(true)
    setError('')
  }

  // Confirm and reset counter
  const handleConfirmReset = async () => {
    setError('')
    setSuccess('')
    setResettingCounter(true)

    try {
      const result = await AdminService.resetPharmacistDailyCounter(selectedPharmacist.id)

      if (result.success) {
        setSuccess(`Daily counter for ${selectedPharmacist.username} reset to 0`)
        setResetCounterModal(false)
        setSelectedPharmacist(null)
        loadPharmacists(true) // Silent refresh
      } else {
        setError(result.error || 'Failed to reset counter')
      }
    } catch (err) {
      setError('An error occurred while resetting counter')
      console.error('Reset counter error:', err)
    } finally {
      setResettingCounter(false)
    }
  }

  // Calculate progress bar color
  const getProgressColor = (counter, limit) => {
    const percentage = (counter / limit) * 100
    if (percentage >= 100) return 'bg-red-500'
    if (percentage >= 80) return 'bg-orange-500'
    return 'bg-green-500'
  }

  // Calculate progress percentage
  const getProgressPercentage = (counter, limit) => {
    const safeLimit = Number(limit || 0)
    if (safeLimit <= 0) return 0
    return Math.min((Number(counter || 0) / safeLimit) * 100, 100)
  }

  const getPharmacistTypeLabel = (type) => type === 'freelance' ? 'Freelance' : 'Full-time'
  const isFreelance = (pharmacist) => pharmacist.pharmacist_type === 'freelance'
  const getFreelanceDisplayLimit = (pharmacist) => {
    const dailyCounter = Number(pharmacist.daily_counter || 0)
    const dailyLimit = Number(pharmacist.daily_limit || 0)

    // Legacy/default full-time limit (50) is not meaningful for freelance.
    // Freelance capacity is set only when she requests +1/+2/+3/+4.
    if (!pharmacist.is_online && dailyLimit > dailyCounter) return dailyCounter
    return Math.max(dailyLimit, dailyCounter)
  }
  const getDisplayDailyLimit = (pharmacist) => (isFreelance(pharmacist) ? getFreelanceDisplayLimit(pharmacist) : Number(pharmacist.daily_limit || 0))
  const getFreelanceRequestLabel = (pharmacist) => {
    const limit = getFreelanceDisplayLimit(pharmacist)
    const counter = Number(pharmacist.daily_counter || 0)
    if (!pharmacist.is_online && limit <= counter) return ' '
    return `Requested ${limit}`
  }

  const sortedPharmacists = getSortedPharmacists()

  return (
    <div className="min-h-screen bg-gray-50" dir="ltr">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin')}
                className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <Users className="w-6 h-6 text-purple-600 mr-2" />
                <h1 className="text-xl font-semibold text-gray-900">Pharmacist Management</h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setSortByStatus(!sortByStatus)}
                className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                title={sortByStatus ? 'Sort alphabetically' : 'Sort by status'}
              >
                <ArrowUpDown className="w-4 h-4 mr-2" />
                {sortByStatus ? 'Online First' : 'A-Z'}
              </button>
              <button
                onClick={() => loadPharmacists()}
                disabled={loading}
                className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
            <p className="text-red-600 flex-1">{error}</p>
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
            <p className="text-green-600 flex-1">{success}</p>
            <button onClick={() => setSuccess('')} className="ml-auto text-green-400 hover:text-green-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Freelance PH</p>
            <p className="text-2xl font-semibold text-purple-700">{pharmacists.filter(p => p.pharmacist_type === 'freelance').length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Online Freelance</p>
            <p className="text-2xl font-semibold text-green-700">{pharmacists.filter(p => p.pharmacist_type === 'freelance' && p.is_online).length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Freelance Taking Today</p>
            <p className="text-2xl font-semibold text-gray-900">{pharmacists.filter(p => p.pharmacist_type === 'freelance').reduce((sum, p) => sum + Number(p.daily_counter || 0), 0)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500">Full-time PH</p>
            <p className="text-2xl font-semibold text-blue-700">{pharmacists.filter(p => p.pharmacist_type !== 'freelance').length}</p>
          </div>
        </div>

        {/* Pharmacists Table */}
        {!loading && sortedPharmacists.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No pharmacists found</p>
          </div>
        )}

        {!loading && sortedPharmacists.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="w-full min-w-[1220px] divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-[22%] px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pharmacist
                  </th>
                  <th className="w-[9%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="w-[9%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="w-[18%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Today's Requests
                  </th>
                  <th className="w-[11%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Daily Limit
                  </th>
                  <th className="w-[16%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    This Hour
                  </th>
                  <th className="w-[9%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hourly Limit
                  </th>
                  {canEditLimits && (
                    <th className="w-[6%] px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedPharmacists.map((pharmacist) => (
                  <tr key={pharmacist.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-medium text-sm">
                            {pharmacist.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{pharmacist.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${pharmacist.pharmacist_type === 'freelance' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                        {getPharmacistTypeLabel(pharmacist.pharmacist_type)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(pharmacist)}
                        disabled={togglingStatus === pharmacist.id}
                        className={`
                          px-3 py-1 rounded-full text-xs font-medium transition-all
                          ${pharmacist.is_online
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }
                          ${togglingStatus === pharmacist.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        {togglingStatus === pharmacist.id ? (
                          <span className="flex items-center">
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            Updating...
                          </span>
                        ) : (
                          <span>{pharmacist.is_online ? 'Online' : 'Offline'}</span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm text-gray-900 mr-3">
                          {pharmacist.daily_counter}/{getDisplayDailyLimit(pharmacist)}
                          {pharmacist.pharmacist_type === 'freelance' && (
                            <span className="ml-2 text-xs text-purple-600">{getFreelanceRequestLabel(pharmacist)}</span>
                          )}
                        </span>
                        <div className="w-28 xl:w-32 bg-gray-200 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full transition-all ${getProgressColor(pharmacist.daily_counter, getDisplayDailyLimit(pharmacist))}`}
                            style={{ width: `${getProgressPercentage(pharmacist.daily_counter, getDisplayDailyLimit(pharmacist))}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {pharmacist.pharmacist_type === 'freelance' ? (
                        <span className="text-xs font-semibold text-purple-700 bg-purple-50 px-2 py-1 rounded-full">Set by request</span>
                      ) : (
                        <span className="text-sm text-gray-900">{pharmacist.daily_limit}</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm text-gray-900 mr-3">
                          {pharmacist.hourly_dispatch_count}/{pharmacist.hourly_limit ?? 12}
                        </span>
                        <div className="w-28 xl:w-32 bg-gray-200 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full transition-all ${getProgressColor(
                              pharmacist.hourly_dispatch_count,
                              pharmacist.hourly_limit ?? 12
                            )}`}
                            style={{
                              width: `${getProgressPercentage(
                                pharmacist.hourly_dispatch_count,
                                pharmacist.hourly_limit ?? 12
                              )}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {pharmacist.pharmacist_type === 'freelance' ? (
                        <span className="text-xs font-semibold text-purple-700 bg-purple-50 px-2 py-1 rounded-full">Unlimited</span>
                      ) : (
                        <span className="text-sm text-gray-900">{pharmacist.hourly_limit ?? 12}</span>
                      )}
                    </td>
                    {canEditLimits && (
                      <td className="px-5 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditLimit(pharmacist)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                          title="Edit Limits"
                        >
                          <Edit2 className="w-4 h-4 inline" />
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleResetCounter(pharmacist)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Reset Counter"
                          >
                            <RotateCcw className="w-4 h-4 inline" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Dispatch Limits Modal */}
      {editLimitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Edit Limits for {selectedPharmacist?.username}
              </h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Daily Limit (1-999)
                </label>
                <input
                  type="number"
                  min="1"
                  max="999"
                  step="1"
                  value={newDailyLimit}
                  onChange={(e) => setNewDailyLimit(e.target.value)}
                  disabled={!canEditDailyLimit}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                  placeholder="Enter daily limit"
                  autoFocus={canEditDailyLimit}
                />
                {!canEditDailyLimit && (
                  <p className="mt-1 text-xs text-gray-500">Only super admins can change daily limit.</p>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hourly Limit (1-999)
                </label>
                <input
                  type="number"
                  min="1"
                  max="999"
                  step="1"
                  value={newHourlyLimit}
                  onChange={(e) => setNewHourlyLimit(e.target.value)}
                  disabled={!canEditHourlyLimit}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                  placeholder="Enter hourly limit"
                  autoFocus={!canEditDailyLimit && canEditHourlyLimit}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setEditLimitModal(false)
                    setSelectedPharmacist(null)
                    setNewDailyLimit('')
                    setNewHourlyLimit('')
                  }}
                  disabled={updatingLimit}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLimit}
                  disabled={updatingLimit}
                  className="px-4 py-2 text-sm text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {updatingLimit ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Counter Modal */}
      {resetCounterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Reset Daily Counter
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to reset the daily counter for <strong>{selectedPharmacist?.username}</strong> from{' '}
                <strong>{selectedPharmacist?.daily_counter}</strong> to <strong>0</strong>?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setResetCounterModal(false)
                    setSelectedPharmacist(null)
                  }}
                  disabled={resettingCounter}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmReset}
                  disabled={resettingCounter}
                  className="px-4 py-2 text-sm text-white bg-orange-600 rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50"
                >
                  {resettingCounter ? 'Resetting...' : 'Confirm Reset'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PharmacistManagement
